import api from '../../../lib/api';
import type {
  CampaignPurpose,
  CampaignDetailMetrics,
  CampaignRecord,
  CampaignAudiencePage,
  CampaignActivationPreview,
  ContactabilityPolicyRecord,
  ContactAgent,
  ContactAgentAvailableBot,
  ContactAgentDashboardMetrics,
  ContactExceptionRecord,
  ContactConversationThread,
  ContactRecord,
  ContactTrackingResult,
  WorkQueueResult,
  CreateCampaignRequest,
  CreateContactAgentRequest,
  UpdateContactAgentRequest,
  CreatePortfolioConnectionRequest,
  UpdatePortfolioConnectionRequest,
  PortfolioConnectionPublic,
  PortfolioPluginManifest,
  ContactExceptionDesk,
  ProactiveTemplateMapEntry,
  ProactiveTemplatePurpose,
  PaymentPromiseListResult,
  PaymentPromiseStatus,
  PortfolioImportMapping,
  PortfolioImportResult,
  SegmentFilter,
  UpdateCampaignAudienceRequest,
  ReplaceCampaignAudienceRequest,
  AudienceReachabilityPreview,
  UnreachableAudiencePage,
  CampaignRepliesPage,
  CampaignTestSendRequest,
  CampaignTestSendResult,
  CreateMessageTemplateRequest,
  MessageTemplateRecord,
  MessageTemplateStatus,
  UpdateMessageTemplateRequest,
  CreateNoticeProgramRequest,
  NoticeDispatchRecord,
  NoticeDispatchResult,
  NoticeProgramRecord,
  NoticeUnreachablePage,
  SetupEventBindingRecord,
  SetupEventDetectorId,
  SetupEventDetectorView,
  UpsertSetupEventBindingRequest,
  UpdateNoticeProgramRequest,
} from '@workspace/types';
import { scopedParams } from './contactAgentScope';

type ApiResponse<T> = {
  success: boolean;
  data?: T;
  error?: string;
};

function requireData<T>(response: { data: ApiResponse<T> }, fallbackError: string): T {
  if (!response.data.success || response.data.data === undefined) {
    throw new Error(response.data.error || fallbackError);
  }
  return response.data.data;
}

export const contactAgentApi = {
  async listAgents(): Promise<ContactAgent[]> {
    const response = await api.get<ApiResponse<ContactAgent[]>>('/admin/contact-agent/agents', {
      params: scopedParams(),
    });
    return requireData(response, 'No se pudieron cargar los agentes');
  },

  async listAvailableBots(): Promise<ContactAgentAvailableBot[]> {
    const response = await api.get<ApiResponse<ContactAgentAvailableBot[]>>(
      '/admin/contact-agent/available-bots',
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudieron cargar los bots disponibles');
  },

  async getAgent(agentId: string): Promise<ContactAgent> {
    const response = await api.get<ApiResponse<ContactAgent>>(
      `/admin/contact-agent/agents/${agentId.slice(0, 64)}`,
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo cargar el agente');
  },

  async createAgent(payload: CreateContactAgentRequest): Promise<ContactAgent> {
    const response = await api.post<ApiResponse<ContactAgent>>(
      '/admin/contact-agent/agents',
      payload,
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo crear el agente');
  },

  async updateAgent(agentId: string, payload: UpdateContactAgentRequest): Promise<ContactAgent> {
    const response = await api.patch<ApiResponse<ContactAgent>>(
      `/admin/contact-agent/agents/${agentId.slice(0, 64)}`,
      payload,
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo actualizar el agente');
  },

  async listContacts(
    limit = 50,
    offset = 0,
    filters?: { optOut?: boolean },
  ): Promise<{ items: ContactRecord[]; total: number }> {
    const response = await api.get<ApiResponse<{ items: ContactRecord[]; total: number }>>(
      '/admin/contact-agent/contacts',
      {
        params: scopedParams({
          limit,
          offset,
          ...(typeof filters?.optOut === 'boolean' ? { optOut: String(filters.optOut) } : {}),
        }),
      },
    );
    return requireData(response, 'No se pudieron cargar los contactos');
  },

  async getContactAccessLink(
    contactId: string,
    agentId: string,
  ): Promise<{ url: string; expiresAt: string }> {
    const response = await api.get<ApiResponse<{ url: string; expiresAt: string }>>(
      `/admin/contact-agent/contacts/${contactId.slice(0, 64)}/access-link`,
      { params: scopedParams({ agentId }) },
    );
    return requireData(response, 'No se pudo obtener el enlace');
  },

  async getWorkQueue(): Promise<WorkQueueResult> {
    const response = await api.get<ApiResponse<WorkQueueResult>>(
      '/admin/contact-agent/workqueue',
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo cargar la bandeja');
  },

  async getContactTracking(contactId: string): Promise<ContactTrackingResult> {
    const response = await api.get<ApiResponse<ContactTrackingResult>>(
      `/admin/contact-agent/contacts/${contactId.slice(0, 64)}/tracking`,
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo cargar la ficha del contacto');
  },

  async getContactThread(
    contactId: string,
    conversationId: string,
  ): Promise<ContactConversationThread> {
    const response = await api.get<ApiResponse<ContactConversationThread>>(
      `/admin/contact-agent/contacts/${contactId.slice(0, 64)}/conversations/${conversationId.slice(0, 64)}/thread`,
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo cargar la conversación');
  },

  async interveneContact(
    contactId: string,
    conversationId?: string,
  ): Promise<{ exceptionId: string; conversationId: string }> {
    const response = await api.post<ApiResponse<{ exceptionId: string; conversationId: string }>>(
      `/admin/contact-agent/contacts/${contactId.slice(0, 64)}/intervene`,
      conversationId ? { conversationId: conversationId.slice(0, 64) } : {},
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo intervenir la conversación');
  },

  async optOutContact(contactId: string, reason?: string): Promise<ContactRecord> {
    const response = await api.post<ApiResponse<ContactRecord>>(
      `/admin/contact-agent/contacts/${contactId.slice(0, 64)}/opt-out`,
      { reason: reason?.slice(0, 255) },
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo dar de baja el contacto');
  },

  async revokeContactOptOut(contactId: string, reason: string): Promise<ContactRecord> {
    const response = await api.post<ApiResponse<ContactRecord>>(
      `/admin/contact-agent/contacts/${contactId.slice(0, 64)}/opt-out/revoke`,
      { reason: reason.slice(0, 255) },
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo revocar la baja');
  },

  async previewImportPortfolio(file: File): Promise<{
    columns: string[];
    sampleRows: Array<Record<string, string>>;
    rowCount: number;
  }> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<
      ApiResponse<{
        columns: string[];
        sampleRows: Array<Record<string, string>>;
        rowCount: number;
      }>
    >('/admin/contact-agent/imports/portfolio/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: scopedParams(),
    });
    return requireData(response, 'No se pudo leer el archivo');
  },

  async importPortfolio(file: File, mapping: PortfolioImportMapping): Promise<PortfolioImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mapping', JSON.stringify(mapping));
    const response = await api.post<ApiResponse<PortfolioImportResult>>(
      '/admin/contact-agent/imports/portfolio',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: scopedParams(),
      },
    );
    return requireData(response, 'No se pudo importar la cartera');
  },

  async listCampaigns(): Promise<CampaignRecord[]> {
    const response = await api.get<ApiResponse<CampaignRecord[]>>('/admin/contact-agent/campaigns', {
      params: scopedParams(),
    });
    return requireData(response, 'No se pudieron cargar las campañas');
  },

  async getCampaign(campaignId: string): Promise<CampaignRecord> {
    const response = await api.get<ApiResponse<CampaignRecord>>(
      `/admin/contact-agent/campaigns/${campaignId.slice(0, 64)}`,
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo cargar la campaña');
  },

  async createCampaign(payload: CreateCampaignRequest): Promise<CampaignRecord> {
    const response = await api.post<ApiResponse<CampaignRecord>>(
      '/admin/contact-agent/campaigns',
      payload,
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo crear la campaña');
  },

  async getAudience(
    campaignId: string,
    params?: { limit?: number; cursor?: string; status?: string },
  ): Promise<CampaignAudiencePage> {
    const response = await api.get<ApiResponse<CampaignAudiencePage>>(
      `/admin/contact-agent/campaigns/${campaignId.slice(0, 64)}/audience`,
      { params: scopedParams(params) },
    );
    return requireData(response, 'No se pudo cargar la audiencia');
  },

  async replaceAudience(campaignId: string, payload: ReplaceCampaignAudienceRequest): Promise<CampaignRecord> {
    const response = await api.put<ApiResponse<CampaignRecord>>(
      `/admin/contact-agent/campaigns/${campaignId.slice(0, 64)}/audience`,
      payload,
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo guardar la audiencia');
  },

  async previewAudienceImport(campaignId: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<
      ApiResponse<{ columns: string[]; sampleRows: Array<Record<string, string>>; rowCount: number }>
    >(`/admin/contact-agent/campaigns/${campaignId.slice(0, 64)}/audience/imports/preview`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: scopedParams(),
    });
    return requireData(response, 'No se pudo leer el archivo');
  },

  async importAudience(
    campaignId: string,
    file: File,
    expectedRevision: number,
    mapping: { phone?: string; contactId?: string; debtId?: string; externalRef?: string },
  ): Promise<CampaignRecord> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('expectedRevision', String(expectedRevision));
    formData.append('mapping', JSON.stringify(mapping));
    const response = await api.post<ApiResponse<CampaignRecord>>(
      `/admin/contact-agent/campaigns/${campaignId.slice(0, 64)}/audience/imports`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: scopedParams(),
      },
    );
    return requireData(response, 'No se pudo importar la audiencia');
  },

  async activationPreview(campaignId: string): Promise<CampaignActivationPreview> {
    const response = await api.post<ApiResponse<CampaignActivationPreview>>(
      `/admin/contact-agent/campaigns/${campaignId.slice(0, 64)}/activation-preview`,
      {},
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo previsualizar la activacion');
  },

  async activateCampaign(
    campaignId: string,
    payload: { previewId: string; audienceRevision: number; audienceHash: string },
  ): Promise<CampaignRecord> {
    const response = await api.post<ApiResponse<CampaignRecord>>(
      `/admin/contact-agent/campaigns/${campaignId.slice(0, 64)}/activate`,
      payload,
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo iniciar la campaña');
  },

  async pauseCampaign(campaignId: string): Promise<CampaignRecord> {
    const response = await api.post<ApiResponse<CampaignRecord>>(
      `/admin/contact-agent/campaigns/${campaignId.slice(0, 64)}/pause`,
      {},
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo pausar la campaña');
  },

  async completeCampaign(campaignId: string): Promise<CampaignRecord> {
    const response = await api.post<ApiResponse<CampaignRecord>>(
      `/admin/contact-agent/campaigns/${campaignId.slice(0, 64)}/complete`,
      {},
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo completar la campaña');
  },

  async cloneCampaign(campaignId: string): Promise<CampaignRecord> {
    const response = await api.post<ApiResponse<CampaignRecord>>(
      `/admin/contact-agent/campaigns/${campaignId.slice(0, 64)}/clone`,
      {},
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo clonar la campaña');
  },

  async listPolicies(): Promise<ContactabilityPolicyRecord[]> {
    const response = await api.get<ApiResponse<ContactabilityPolicyRecord[]>>(
      '/admin/contact-agent/contactability-policies',
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudieron cargar las politicas');
  },

  async upsertPolicy(payload: {
    purpose: CampaignPurpose;
    legalBasis: string;
    requiresConsent: boolean;
    evidenceRef?: string;
  }): Promise<ContactabilityPolicyRecord> {
    const response = await api.post<ApiResponse<ContactabilityPolicyRecord>>(
      '/admin/contact-agent/contactability-policies',
      payload,
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo guardar la politica');
  },

  async updateCampaignStatus(campaignId: string, status: CampaignRecord['status']) {
    const response = await api.patch<ApiResponse<CampaignRecord>>(
      `/admin/contact-agent/campaigns/${campaignId.slice(0, 64)}/status`,
      { status },
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo actualizar la campaña');
  },

  async updateCampaignAudience(campaignId: string, payload: UpdateCampaignAudienceRequest) {
    const response = await api.patch<ApiResponse<CampaignRecord>>(
      `/admin/contact-agent/campaigns/${campaignId.slice(0, 64)}`,
      payload,
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo actualizar el publico');
  },

  async deleteCampaign(campaignId: string) {
    const response = await api.delete<ApiResponse<{ id: string }>>(
      `/admin/contact-agent/campaigns/${campaignId.slice(0, 64)}`,
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo eliminar la campaña');
  },

  async previewSegment(
    filter: SegmentFilter,
    purpose?: CampaignPurpose,
    agentId?: string,
  ): Promise<{ count: number }> {
    const response = await api.post<ApiResponse<{ count: number }>>(
      '/admin/contact-agent/campaigns/segment-preview',
      { ...filter, purpose, agentId },
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo calcular el segmento');
  },

  async previewReachability(payload: {
    purpose?: CampaignPurpose;
    channelKind?: CampaignRecord['channelKind'];
    agentId?: string;
    segmentFilter?: SegmentFilter;
    audienceMode?: CampaignRecord['audienceMode'];
    contactIds?: string[];
  }): Promise<AudienceReachabilityPreview> {
    const response = await api.post<ApiResponse<AudienceReachabilityPreview>>(
      '/admin/contact-agent/campaigns/audience/reachability-preview',
      payload,
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo calcular el alcance');
  },

  async getUnreachable(
    campaignId: string,
    query?: { reason?: 'MISSING_EMAIL' | 'INVALID_PHONE'; cursor?: string; limit?: number },
  ): Promise<UnreachableAudiencePage> {
    const response = await api.get<ApiResponse<UnreachableAudiencePage>>(
      `/admin/contact-agent/campaigns/${campaignId.slice(0, 64)}/audience/unreachable`,
      { params: scopedParams(query) },
    );
    return requireData(response, 'No se pudo cargar el listado de datos faltantes');
  },

  async getCampaignReplies(
    campaignId: string,
    query?: { cursor?: string; limit?: number },
  ): Promise<CampaignRepliesPage> {
    const response = await api.get<ApiResponse<CampaignRepliesPage>>(
      `/admin/contact-agent/campaigns/${campaignId.slice(0, 64)}/replies`,
      { params: scopedParams(query) },
    );
    return requireData(response, 'No se pudieron cargar las respuestas');
  },

  async reevaluateAudience(campaignId: string): Promise<CampaignRecord> {
    const response = await api.post<ApiResponse<CampaignRecord>>(
      `/admin/contact-agent/campaigns/${campaignId.slice(0, 64)}/audience/reevaluate`,
      {},
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo reevaluar la audiencia');
  },

  async updateContact(
    contactId: string,
    payload: { email?: string; firstName?: string; lastName?: string; businessName?: string },
  ): Promise<ContactRecord> {
    const response = await api.patch<ApiResponse<ContactRecord>>(
      `/admin/contact-agent/contacts/${contactId.slice(0, 64)}`,
      payload,
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo actualizar el contacto');
  },

  async getCampaignMetrics(campaignId: string): Promise<CampaignDetailMetrics> {
    const response = await api.get<ApiResponse<CampaignDetailMetrics>>(
      `/admin/contact-agent/campaigns/${campaignId.slice(0, 64)}/metrics`,
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudieron cargar las metricas de la campaña');
  },

  async getDashboard(): Promise<ContactAgentDashboardMetrics> {
    const response = await api.get<ApiResponse<ContactAgentDashboardMetrics>>(
      '/admin/contact-agent/dashboard',
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudieron cargar las metricas');
  },

  async listExceptions(
    status?: ContactExceptionRecord['status'],
    options?: { includeClosed?: boolean },
  ): Promise<ContactExceptionRecord[]> {
    const response = await api.get<ApiResponse<ContactExceptionRecord[]>>(
      '/admin/contact-agent/exceptions',
      {
        params: scopedParams({
          ...(status ? { status } : {}),
          ...(options?.includeClosed ? { includeClosed: 'true' } : {}),
        }),
      },
    );
    return requireData(response, 'No se pudieron cargar las excepciones');
  },

  async listPaymentPromises(options?: {
    limit?: number;
    offset?: number;
    status?: PaymentPromiseStatus;
    origin?: 'self' | 'campaign' | 'all';
  }): Promise<PaymentPromiseListResult> {
    const response = await api.get<ApiResponse<PaymentPromiseListResult>>(
      '/admin/contact-agent/payment-promises',
      {
        params: scopedParams({
          ...(options?.limit ? { limit: String(options.limit) } : {}),
          ...(options?.offset ? { offset: String(options.offset) } : {}),
          ...(options?.status ? { status: options.status } : {}),
          ...(options?.origin ? { origin: options.origin } : {}),
        }),
      },
    );
    return requireData(response, 'No se pudieron cargar los compromisos de pago');
  },

  async takeException(exceptionId: string): Promise<ContactExceptionRecord> {
    const response = await api.post<ApiResponse<ContactExceptionRecord>>(
      `/admin/contact-agent/exceptions/${exceptionId.slice(0, 64)}/take`,
      {},
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo tomar la excepcion');
  },

  async getException(exceptionId: string): Promise<ContactExceptionDesk> {
    const response = await api.get<ApiResponse<ContactExceptionDesk>>(
      `/admin/contact-agent/exceptions/${exceptionId.slice(0, 64)}`,
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo cargar la excepcion');
  },

  async replyException(exceptionId: string, text: string): Promise<ContactExceptionDesk> {
    const response = await api.post<ApiResponse<ContactExceptionDesk>>(
      `/admin/contact-agent/exceptions/${exceptionId.slice(0, 64)}/reply`,
      { text: text.slice(0, 4096) },
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo enviar el mensaje');
  },

  async confirmExceptionPayment(exceptionId: string, paymentId: string): Promise<ContactExceptionDesk> {
    const response = await api.post<ApiResponse<ContactExceptionDesk>>(
      `/admin/contact-agent/exceptions/${exceptionId.slice(0, 64)}/confirm-payment`,
      { paymentId },
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo confirmar el pago');
  },

  async rejectExceptionPayment(
    exceptionId: string,
    paymentId: string,
    reason?: string,
  ): Promise<ContactExceptionDesk> {
    const response = await api.post<ApiResponse<ContactExceptionDesk>>(
      `/admin/contact-agent/exceptions/${exceptionId.slice(0, 64)}/reject-payment`,
      { paymentId, ...(reason ? { reason: reason.slice(0, 300) } : {}) },
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo rechazar el pago');
  },

  async listProactiveTemplates(): Promise<ProactiveTemplateMapEntry[]> {
    const response = await api.get<ApiResponse<ProactiveTemplateMapEntry[]>>(
      '/admin/contact-agent/proactive-templates',
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudieron cargar los mapeos de plantillas');
  },

  async upsertProactiveTemplate(input: {
    channelId: string;
    purpose: ProactiveTemplatePurpose;
    templateId: string;
  }): Promise<ProactiveTemplateMapEntry> {
    const response = await api.put<ApiResponse<ProactiveTemplateMapEntry>>(
      '/admin/contact-agent/proactive-templates',
      input,
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo guardar el mapeo');
  },

  async deleteProactiveTemplate(mapId: string): Promise<void> {
    await api.delete<ApiResponse<null>>(
      `/admin/contact-agent/proactive-templates/${mapId.slice(0, 64)}`,
      { params: scopedParams() },
    );
  },

  async closeException(exceptionId: string, resumeBot: boolean): Promise<ContactExceptionDesk> {
    const response = await api.post<ApiResponse<ContactExceptionDesk>>(
      `/admin/contact-agent/exceptions/${exceptionId.slice(0, 64)}/close`,
      { resumeBot },
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo cerrar la excepcion');
  },

  async listPortfolioPlugins(): Promise<PortfolioPluginManifest[]> {
    const response = await api.get<ApiResponse<PortfolioPluginManifest[]>>(
      '/admin/contact-agent/portfolio-plugins',
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudieron cargar los plugins');
  },

  async listPortfolioConnections(): Promise<PortfolioConnectionPublic[]> {
    const response = await api.get<ApiResponse<PortfolioConnectionPublic[]>>(
      '/admin/contact-agent/portfolio-connections',
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudieron cargar las conexiones');
  },

  async createPortfolioConnection(
    payload: CreatePortfolioConnectionRequest,
  ): Promise<PortfolioConnectionPublic> {
    const response = await api.post<ApiResponse<PortfolioConnectionPublic>>(
      '/admin/contact-agent/portfolio-connections',
      payload,
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo crear la conexion');
  },

  async updatePortfolioConnection(
    connectionId: string,
    payload: UpdatePortfolioConnectionRequest,
  ): Promise<PortfolioConnectionPublic> {
    const response = await api.patch<ApiResponse<PortfolioConnectionPublic>>(
      `/admin/contact-agent/portfolio-connections/${connectionId.slice(0, 64)}`,
      payload,
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo actualizar la conexion');
  },

  async testPortfolioConnection(connectionId: string): Promise<void> {
    const response = await api.post<ApiResponse<{ ok: boolean }>>(
      `/admin/contact-agent/portfolio-connections/${connectionId.slice(0, 64)}/test`,
      {},
      { params: scopedParams() },
    );
    requireData(response, 'No se pudo probar la conexion');
  },

  async syncPortfolioConnection(
    connectionId: string,
    options: { confirmMassClose?: boolean } = {},
  ): Promise<PortfolioConnectionPublic> {
    const response = await api.post<ApiResponse<PortfolioConnectionPublic>>(
      `/admin/contact-agent/portfolio-connections/${connectionId.slice(0, 64)}/sync`,
      options.confirmMassClose ? { confirmMassClose: true } : {},
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo sincronizar la cartera');
  },

  async listSetupEventDetectors(connectionId: string): Promise<SetupEventDetectorView[]> {
    const response = await api.get<ApiResponse<SetupEventDetectorView[]>>(
      `/admin/contact-agent/portfolio-connections/${connectionId.slice(0, 64)}/event-detectors`,
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudieron cargar los eventos de Setup');
  },

  async upsertSetupEventBinding(
    connectionId: string,
    detectorId: SetupEventDetectorId,
    payload: UpsertSetupEventBindingRequest,
  ): Promise<SetupEventBindingRecord> {
    const response = await api.put<ApiResponse<SetupEventBindingRecord>>(
      `/admin/contact-agent/portfolio-connections/${connectionId.slice(0, 64)}/event-bindings/${detectorId}`,
      payload,
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo guardar el evento');
  },

  async sweepSetupEventBinding(
    connectionId: string,
    detectorId: SetupEventDetectorId,
  ): Promise<SetupEventBindingRecord> {
    const response = await api.post<ApiResponse<SetupEventBindingRecord>>(
      `/admin/contact-agent/portfolio-connections/${connectionId.slice(0, 64)}/event-bindings/${detectorId}/sweep`,
      {},
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo barrer el evento');
  },

  async testSendDraft(
    payload: CampaignTestSendRequest & { agentId?: string },
  ): Promise<CampaignTestSendResult> {
    const response = await api.post<ApiResponse<CampaignTestSendResult>>(
      '/admin/contact-agent/campaigns/test-send',
      payload,
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo enviar la prueba');
  },

  async testSend(campaignId: string, payload: CampaignTestSendRequest): Promise<CampaignTestSendResult> {
    const response = await api.post<ApiResponse<CampaignTestSendResult>>(
      `/admin/contact-agent/campaigns/${campaignId.slice(0, 64)}/test-send`,
      payload,
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo enviar la prueba');
  },

  async deleteTestConversation(conversationId: string): Promise<{ conversationId: string }> {
    const response = await api.delete<ApiResponse<{ conversationId: string }>>(
      `/admin/contact-agent/conversations/test/${conversationId.slice(0, 64)}`,
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo borrar el hilo de prueba');
  },

  async listMessageTemplates(params?: {
    channelKind?: 'SMS' | 'EMAIL';
    status?: MessageTemplateStatus;
  }): Promise<MessageTemplateRecord[]> {
    const response = await api.get<ApiResponse<MessageTemplateRecord[]>>(
      '/admin/contact-agent/message-templates',
      { params: scopedParams(params) },
    );
    return requireData(response, 'No se pudieron cargar las plantillas');
  },

  async getMessageTemplate(templateId: string): Promise<MessageTemplateRecord> {
    const response = await api.get<ApiResponse<MessageTemplateRecord>>(
      `/admin/contact-agent/message-templates/${templateId.slice(0, 64)}`,
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo cargar la plantilla');
  },

  async createMessageTemplate(payload: CreateMessageTemplateRequest): Promise<MessageTemplateRecord> {
    const response = await api.post<ApiResponse<MessageTemplateRecord>>(
      '/admin/contact-agent/message-templates',
      payload,
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo crear la plantilla');
  },

  async updateMessageTemplate(
    templateId: string,
    payload: UpdateMessageTemplateRequest,
  ): Promise<MessageTemplateRecord> {
    const response = await api.patch<ApiResponse<MessageTemplateRecord>>(
      `/admin/contact-agent/message-templates/${templateId.slice(0, 64)}`,
      payload,
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo guardar la plantilla');
  },

  async duplicateMessageTemplate(templateId: string): Promise<MessageTemplateRecord> {
    const response = await api.post<ApiResponse<MessageTemplateRecord>>(
      `/admin/contact-agent/message-templates/${templateId.slice(0, 64)}/duplicate`,
      {},
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo duplicar la plantilla');
  },

  async archiveMessageTemplate(templateId: string): Promise<MessageTemplateRecord> {
    const response = await api.post<ApiResponse<MessageTemplateRecord>>(
      `/admin/contact-agent/message-templates/${templateId.slice(0, 64)}/archive`,
      {},
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo archivar la plantilla');
  },

  async listNoticePrograms(): Promise<NoticeProgramRecord[]> {
    const response = await api.get<ApiResponse<NoticeProgramRecord[]>>(
      '/admin/contact-agent/notice-programs',
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudieron cargar los programas de disparo');
  },

  async getNoticeProgram(programId: string): Promise<NoticeProgramRecord> {
    const response = await api.get<ApiResponse<NoticeProgramRecord>>(
      `/admin/contact-agent/notice-programs/${programId.slice(0, 64)}`,
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo cargar el programa');
  },

  async createNoticeProgram(payload: CreateNoticeProgramRequest): Promise<NoticeProgramRecord> {
    const response = await api.post<ApiResponse<NoticeProgramRecord>>(
      '/admin/contact-agent/notice-programs',
      payload,
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo crear el programa');
  },

  async updateNoticeProgram(
    programId: string,
    payload: UpdateNoticeProgramRequest,
  ): Promise<NoticeProgramRecord> {
    const response = await api.patch<ApiResponse<NoticeProgramRecord>>(
      `/admin/contact-agent/notice-programs/${programId.slice(0, 64)}`,
      payload,
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo guardar el programa');
  },

  async activateNoticeProgram(programId: string): Promise<NoticeProgramRecord> {
    const response = await api.post<ApiResponse<NoticeProgramRecord>>(
      `/admin/contact-agent/notice-programs/${programId.slice(0, 64)}/activate`,
      {},
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo activar el programa');
  },

  async pauseNoticeProgram(programId: string): Promise<NoticeProgramRecord> {
    const response = await api.post<ApiResponse<NoticeProgramRecord>>(
      `/admin/contact-agent/notice-programs/${programId.slice(0, 64)}/pause`,
      {},
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo pausar el programa');
  },

  async completeNoticeProgram(programId: string): Promise<NoticeProgramRecord> {
    const response = await api.post<ApiResponse<NoticeProgramRecord>>(
      `/admin/contact-agent/notice-programs/${programId.slice(0, 64)}/complete`,
      {},
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo completar el programa');
  },

  async deleteNoticeProgram(programId: string): Promise<void> {
    const response = await api.delete<ApiResponse<void>>(
      `/admin/contact-agent/notice-programs/${programId.slice(0, 64)}`,
      { params: scopedParams() },
    );
    if (!response.data.success) {
      throw new Error(response.data.error || 'No se pudo eliminar el programa');
    }
  },

  async listNoticeDispatches(
    programId: string,
    params?: { status?: string; limit?: number; offset?: number },
  ): Promise<{ items: NoticeDispatchRecord[]; total: number }> {
    const response = await api.get<ApiResponse<{ items: NoticeDispatchRecord[]; total: number }>>(
      `/admin/contact-agent/notice-programs/${programId.slice(0, 64)}/dispatches`,
      { params: scopedParams(params) },
    );
    return requireData(response, 'No se pudieron cargar los disparos');
  },

  async listNoticeUnreachable(
    programId: string,
    params?: { reason?: 'MISSING_EMAIL' | 'INVALID_PHONE'; limit?: number; offset?: number },
  ): Promise<NoticeUnreachablePage> {
    const response = await api.get<ApiResponse<NoticeUnreachablePage>>(
      `/admin/contact-agent/notice-programs/${programId.slice(0, 64)}/dispatches/unreachable`,
      { params: scopedParams(params) },
    );
    return requireData(response, 'No se pudieron cargar los faltantes');
  },

  async retryNoticeDispatch(programId: string, dispatchId: string): Promise<NoticeDispatchResult> {
    const response = await api.post<ApiResponse<NoticeDispatchResult>>(
      `/admin/contact-agent/notice-programs/${programId.slice(0, 64)}/dispatches/${dispatchId.slice(0, 64)}/retry`,
      {},
      { params: scopedParams() },
    );
    return requireData(response, 'No se pudo reintentar el disparo');
  },
};
